import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  DownloadIcon,
  FilePenIcon,
  FileTextIcon,
  FileTypeIcon,
  Redo2Icon,
  SaveIcon,
  SettingsIcon,
  Trash2Icon,
  Undo2Icon,
  UploadIcon,
} from "@lucide/icons";

import { formatDate } from "simplydo/core/dates";
import { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useTrayStatus } from "components/lib/Notifications";
import { Sticky } from "components/lib/UI";

import ImportWordModal from "./ImportWordModal";
import { useIdea } from "./hooks/useIdea";

const IdeaToolbarDiv = styled.div`
  min-height: ${({ theme }) => (theme.sizes.isMobile ? 30 : 40)}px;
  background-color: #f0f0f0;
  border-bottom: 1px solid lightgray;
  top: 50px;
  width: 100%;
`;

const ToolbarContainer = styled(Container)`
  padding-top: 5px;
  padding-bottom: 5px;
  display: flex;
  flex-direction: row-reverse !important;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const HorizontalDiv = styled.div<{
  $verticalAlign?: string;
  $horizontalAlign?: string;
}>`
  display: flex;
  flex-direction: row;
  justify-content: ${({ $verticalAlign }) => $verticalAlign || "flex-start"};
  align-items: ${({ $horizontalAlign }) => $horizontalAlign || "center"};
  flex-wrap: wrap;
`;

type IdeaEditToolbarProps = {
  isEditing: boolean;
  undoChanges: () => void;
  redoChanges: () => void;
  canUndo: boolean;
  canRedo: boolean;
  savedAt: string | Date | "";
  deleteIdea: () => void;
  reloadIdea: () => void;
};

const IdeaEditToolbar = ({
  isEditing,
  undoChanges,
  redoChanges,
  canUndo,
  canRedo,
  savedAt,
  deleteIdea,
  reloadIdea,
}: IdeaEditToolbarProps) => {
  const [idea] = useIdea();
  const { t } = useTranslation();
  const theme = useTheme();
  const [_, setTrayStatus] = useTrayStatus();
  const user = useAppSelector((state) => state.user);
  const [importWordModalOpen, setImportWordModalOpen] = useState(false);

  const generatePDF = useCallback(() => {
    if (!user?._id || !idea?._id) {
      return;
    }
    const challengeName = idea?.ownerChallenge?.name || "Challenge";
    const displayName = `${challengeName} idea export`;

    api.users.createDownload(
      user._id,
      {
        downloadType: "ideaPdfExport",
        displayName: `${displayName}.pdf`,
        ideas: [idea?._id],
        challenge: idea?.ownerChallenge?._id,
      },
      (download) => {
        api.setQueryData(
          api.users.getDownloads,
          [user._id],
          (prev) => ({
            ...prev,
            pendingDownloads: (prev?.pendingDownloads || []).concat([download]),
          }),
          { allowUndefined: true },
        );
        setTrayStatus({ open: true, activeIndex: 1 });
      },
      () => {},
    );
  }, [idea, user, setTrayStatus]);

  const downloadIdeaDocxReport = () => {
    if (!user?._id) {
      return;
    }
    const ideaName = idea?.name || "Idea";
    api.users.createDownload(
      user._id,
      {
        downloadType: "ideaDocxExport",
        displayName: `${ideaName} export.docx`,
        idea: idea?._id,
      },
      (download) => {
        api.setQueryData(
          api.users.getDownloads,
          [user._id],
          (prev) => ({
            ...prev,
            pendingDownloads: (prev?.pendingDownloads || []).concat([download]),
          }),
          { allowUndefined: true },
        );
        setTrayStatus({ open: true, activeIndex: 1 });
      },
      () => {},
    );
  };
  const canExportWhileSubmitted = useMemo(() => {
    return (
      util.hasPermission(
        user,
        "org.manageChallenges",
        idea?.ownerChallenge?.organisation,
      ) || util.hasPermission(user, "challenge.editIdeas", idea?.challenge)
    );
  }, [user, idea]);

  const canImportFromWord = useMemo(
    () =>
      util.canEditIdea(user, idea) &&
      user?.ownerOrganisation?.superadminTags?.innovationType ===
        "externalInnovation",
    [user, idea],
  );
  if (!isEditing) return null;
  return (
    <Sticky top={theme.sizes.isMobile ? 90 : 100} style={{ zIndex: 90 }}>
      <ImportWordModal
        open={importWordModalOpen}
        onClose={() => setImportWordModalOpen(false)}
        ideaId={idea?._id}
        onImported={() => reloadIdea?.()}
      />
      <IdeaToolbarDiv>
        <ToolbarContainer>
          <HorizontalDiv $verticalAlign="space-between">
            <Popup
              content={t("ideas.undo")}
              on="hover"
              trigger={
                <Button
                  size="small"
                  onClick={undoChanges}
                  disabled={!canUndo}
                  variant="outline"
                >
                  <Icon icon={Undo2Icon} />
                </Button>
              }
            />
            <Popup
              content={t("ideas.redo")}
              on="hover"
              trigger={
                <Button
                  size="small"
                  onClick={redoChanges}
                  disabled={!canRedo}
                  variant="outline"
                >
                  <Icon icon={Redo2Icon} />
                </Button>
              }
            />
            <Dropdown
              trigger={
                <Button size="small" variant="outline">
                  <Icon icon={SettingsIcon} />
                </Button>
              }
              direction="left"
              options={[
                {
                  key: "export",
                  value: "export",
                  icon: <Icon icon={FileTextIcon} />,
                  onClick: () => generatePDF(),
                  text: t("ideas.download.pdf"),
                },

                {
                  key: "export-word",
                  value: "export-word",
                  icon: <Icon icon={FilePenIcon} />,
                  text: t("ideas.download.word"),
                  onClick: () => downloadIdeaDocxReport(),
                  disabled: idea?.isSubmitted && !canExportWhileSubmitted,
                },
                ...(canImportFromWord
                  ? [
                      {
                        key: "import-word",
                        value: "import-word",
                        icon: <Icon icon={UploadIcon} />,
                        children: (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            {t("ideas.importWord.menuItem")}
                            <Label size="small" color="violet">
                              {t("generic.beta")}
                            </Label>
                          </span>
                        ),
                        onClick: () => setImportWordModalOpen(true),
                        disabled: idea?.isSubmitted,
                      },
                    ]
                  : []),
                ...(!idea?.ownerChallenge?.preventIdeaCoverImages &&
                idea?.coverImage
                  ? [
                      {
                        key: "download-cover-image",
                        value: "download-cover-image",
                        icon: <Icon icon={DownloadIcon} />,
                        text: t("ideas.download.coverImage"),
                        onClick: () => {
                          if (idea?.coverImageUrl) {
                            window.open(
                              idea.coverImageUrl,
                              "_blank",
                              "noopener noreferrer",
                            );
                          }
                        },
                      },
                    ]
                  : []),
                {
                  key: "delete-idea",
                  value: "delete-idea",
                  text: t("ideas.delete"),
                  icon: <Icon icon={Trash2Icon} />,
                  onClick: () => deleteIdea(),
                },
              ]}
            />
            {util.canEditIdea(user, idea) &&
              !idea?.isSubmitted &&
              !theme.sizes.isMobile && (
                <Button
                  size="small"
                  onClick={() => {
                    toast(t("ideas.autosave.warning"));
                  }}
                >
                  <Icon icon={SaveIcon} />
                  {savedAt
                    ? t("generic.savedAt", {
                        time: formatDate(savedAt, "HH:mm"),
                      })
                    : t("generic.save")}
                </Button>
              )}
          </HorizontalDiv>
        </ToolbarContainer>
      </IdeaToolbarDiv>
    </Sticky>
  );
};

export default IdeaEditToolbar;
